/**
 * RiderApp QR URL builders (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"QR contracts").
 *
 * Every QR the DriverApp shows points at the fleet's RiderApp `/pay` page. The
 * RiderApp resolves the active trip, onboards the rider if needed, shows the
 * fare/tip and only then opens the HBSS hosted page. The DriverApp never puts a
 * payment URL, fare, tip, token or socket address in a QR — only an identifier.
 *
 * Base URL: `https://<fleetcode-lowercase>.riderapp.net/pay`. An explicitly
 * configured `riderAppUrl` (fleet config) is honoured only as the override for
 * an approved nonstandard RiderApp host; it is never derived from fleetUrl.
 *
 * | Presentation        | Query               |
 * | ------------------- | ------------------- |
 * | Dynamic payment QR  | ?tripId=<tripId>    | Live Dispatch / Itinerary payment sheet
 * | Static vehicle QR   | ?vehicleId=<id>     | Dashboard / printed sticker
 * | Static driver QR    | ?driverId=<id>      | Dashboard / business card
 */

const RIDERAPP_HOST_SUFFIX = '.riderapp.net';
const PAY_PATH = '/pay';

function text(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * Resolve the RiderApp `/pay` base for a fleet.
 * @returns {string|null} e.g. "https://acet.riderapp.net/pay", or null when neither a
 *   fleet code nor an override is available.
 */
export function riderAppPayBaseUrl({ fleetCode, riderAppUrl } = {}) {
  const override = text(riderAppUrl);
  if (override) {
    const trimmed = override.replace(/\/+$/, '');
    return /\/pay$/i.test(trimmed) ? trimmed : `${trimmed}${PAY_PATH}`;
  }
  const code = text(fleetCode).toLowerCase();
  if (!code) return null;
  return `https://${code}${RIDERAPP_HOST_SUFFIX}${PAY_PATH}`;
}

function withQuery(base, params) {
  const url = new URL(base);
  // URLSearchParams escapes every value — never concatenate ids by hand.
  Object.keys(params).forEach((key) => url.searchParams.set(key, params[key]));
  return url.toString();
}

/**
 * Dynamic QR for the trip currently being collected. Encodes ONLY the trip id
 * (AutoDispatch `Trip.Id`, the Live JOB `info.jobID` / Itinerary `reservation.id`)
 * — never the short TripNo, and never vehicle/driver ids, fare, tip or tokens.
 * @returns {string|null}
 */
export function buildRiderAppTripPaymentUrl({ fleetCode, riderAppUrl, tripId } = {}) {
  const base = riderAppPayBaseUrl({ fleetCode, riderAppUrl });
  const id = text(tripId);
  if (!base || !id) return null;
  return withQuery(base, { tripId: id });
}

/**
 * Static lookup QR for the Dashboard. A vehicle code wins when a vehicle is
 * selected (sticker in the passenger area); otherwise a driver-only code
 * (business card). RiderApp finds the active trip serviced by that unit, or
 * falls back to New Trip pinned to it.
 * @returns {{ url: string, kind: 'vehicle'|'driver' }|null}
 */
export function buildRiderAppStaticQrUrl({ fleetCode, riderAppUrl, vehicleId, driverId } = {}) {
  const base = riderAppPayBaseUrl({ fleetCode, riderAppUrl });
  if (!base) return null;
  const vehicle = text(vehicleId);
  if (vehicle) return { url: withQuery(base, { vehicleId: vehicle }), kind: 'vehicle' };
  const driver = text(driverId);
  if (driver) return { url: withQuery(base, { driverId: driver }), kind: 'driver' };
  return null;
}

export default buildRiderAppTripPaymentUrl;
